import { resolveFrontendApiBase } from './apiBase'

export interface AIModel {
  id: string
  displayName: string
  apiModel?: string
  provider?: string
  displayOrder?: number
  defaultForWorkflows?: string[]
  launchReady?: boolean
  translationReasoningEffort?: string
  deprecationDate?: string
  depreciationDate?: string
  defaultTier: 'Best' | 'Better' | 'Good' | 'None'
  tags?: string[]
  allowsNSFW?: boolean
  discount?: number
  premium?: number
  isActive: boolean
  testingOnly?: boolean
  internalOnly?: boolean
  isFree?: boolean
  languageTiers?: Record<string, 'Best' | 'Better' | 'Good' | 'None'>
  languagePairTiers?: Record<string, Record<string, 'Best' | 'Better' | 'Good' | 'None'>>
  languageTierLocks?: Record<string, { locked?: boolean; reason?: string | null }>
  // New: pricing per 1M tokens
  inputCostPerMTokens?: number
  cachedInputCostPerMTokens?: number
  outputCostPerMTokens?: number
  // New: disable model for specific languages
  disabledLanguages?: string[]
}

export interface LanguageModelTiers {
  [language: string]: {
    Best?: string[]
    Better?: string[]
    Good?: string[]
    None?: string[]
  }
}

export interface LanguagePairModelTiers {
  [targetLanguage: string]: {
    [sourceLanguage: string]: {
      Best?: string[]
      Better?: string[]
      Good?: string[]
      None?: string[]
    }
  }
}

// Normalize language labels and handle common misspellings/aliases
export function normalizeLanguageName(name?: string): string {
  const raw = (name || '').trim().toLowerCase()
  if (!raw) return ''
  const aliasMap: Record<string, string> = {
    // common misspelling
    'ukranian': 'ukrainian',
  }
  return aliasMap[raw] || raw
}

function resolveLanguageKey<T>(
  map: Record<string, T> | undefined,
  language?: string
): string | undefined {
  if (!language || !map) return undefined
  if (language in map) return language

  const normalized = normalizeLanguageName(language)
  if (normalized && normalized in map) return normalized

  const matchedKey = Object.keys(map).find(
    key => normalizeLanguageName(key) === normalized
  )
  return matchedKey
}

export function isModelDisabledInLanguage(model: AIModel, language?: string): boolean {
  if (!language) return false
  const normLang = normalizeLanguageName(language)
  const disabled = (model.disabledLanguages || []).map(normalizeLanguageName)
  return disabled.includes(normLang)
}

class AIModelService {
  private modelsCache: AIModel[] = []
  private languageTiersCache: LanguageModelTiers = {}
  private languagePairTiersCache: LanguagePairModelTiers = {}
  private lastCacheUpdate: Date | null = null
  private cacheExpiry = 3_600_000 // 1 hour in milliseconds
  private loadingPromise: Promise<void> | null = null
  
  private async fetchAllModels(): Promise<AIModel[]> {
    const resp = await fetch(`${resolveFrontendApiBase()}/api/ai-models`)
    if (!resp.ok) {
      throw new Error(`Failed to fetch AI models: ${resp.status} ${resp.statusText}`)
    }
    return (await resp.json()) as AIModel[]
  }

async loadModels(forceRefresh: boolean = false): Promise<void> {
  const cacheIsFresh =
    !forceRefresh &&
    this.lastCacheUpdate &&
    Date.now() - this.lastCacheUpdate.getTime() < this.cacheExpiry &&
    this.modelsCache.length > 0

  if (cacheIsFresh) {
    return
  }

  if (this.loadingPromise) {
    await this.loadingPromise
    return
  }

  this.loadingPromise = (async () => {
    try {
      const all = await this.fetchAllModels()

      // filter active models; keep original tags and pricing adjustment labels
      this.modelsCache = all
        .filter(m => m.isActive && !m.internalOnly)
        .map(m => {
          const { discount = 0, premium = 0, tags = [], ...rest } = m

          return {
            ...rest,
            ...(discount > 0 ? { discount } : {}),
            ...(premium > 0 ? { premium } : {}),
            tags,
          } as AIModel
        })

      // rebuild language tier caches from each model's overrides
      this.languageTiersCache = {}
      this.languagePairTiersCache = {}

      this.modelsCache.forEach(model => {
        if (model.languageTiers) {
          Object.entries(model.languageTiers).forEach(([lang, tier]) => {
            if (!tier || tier === 'None') return
            const key = normalizeLanguageName(lang) || lang.trim()
            if (!key) return
            if (!this.languageTiersCache[key]) {
              this.languageTiersCache[key] = {}
            }
            if (!this.languageTiersCache[key][tier]) {
              this.languageTiersCache[key][tier] = []
            }
            const list = this.languageTiersCache[key][tier]!
            if (!list.includes(model.id)) {
              list.push(model.id)
            }
          })
        }

        if (model.languagePairTiers) {
          Object.entries(model.languagePairTiers).forEach(([target, sources]) => {
            if (!sources) return
            const targetKey = normalizeLanguageName(target) || target.trim()
            if (!targetKey) return
            if (!this.languagePairTiersCache[targetKey]) {
              this.languagePairTiersCache[targetKey] = {}
            }
            Object.entries(sources).forEach(([source, tier]) => {
              if (!tier || tier === 'None') return
              const sourceKey = normalizeLanguageName(source) || source.trim()
              if (!sourceKey) return
              if (!this.languagePairTiersCache[targetKey]![sourceKey]) {
                this.languagePairTiersCache[targetKey]![sourceKey] = {}
              }
              if (!this.languagePairTiersCache[targetKey]![sourceKey]![tier]) {
                this.languagePairTiersCache[targetKey]![sourceKey]![tier] = []
              }
              const list = this.languagePairTiersCache[targetKey]![sourceKey]![tier]!
              if (!list.includes(model.id)) {
                list.push(model.id)
              }
            })
          })
        }
      })

      this.lastCacheUpdate = new Date()
    } catch (error) {
      console.error('Error loading AI models:', error)
      throw error
    } finally {
      this.loadingPromise = null
    }
  })()

  await this.loadingPromise
}

  async getActiveModels(isLangTesterUser = false, forceRefresh: boolean = false): Promise<AIModel[]> {
    await this.loadModels(forceRefresh)
    return this.modelsCache.filter(model => !model.testingOnly || isLangTesterUser)
  }

  async getModelById(id: string, forceRefresh: boolean = false): Promise<AIModel | undefined> {
    await this.loadModels(forceRefresh)
    return this.modelsCache.find(m => m.id === id)
  }

  async getModelsByTier(
    tier: 'Best' | 'Better' | 'Good' | 'None',
    language?: string,
    isLangTesterUser = false,
    sourceLanguage?: string,
    forceRefresh: boolean = false
  ): Promise<AIModel[]> {
    const active = await this.getActiveModels(isLangTesterUser, forceRefresh)
    const activeById = new Map(active.map(model => [model.id, model]))
    const seen = new Set<string>()
    const ordered: AIModel[] = []

    const appendByIds = (ids?: string[]) => {
      if (!ids || ids.length === 0) return
      ids.forEach(id => {
        if (seen.has(id)) return
        const model = activeById.get(id)
        if (!model) return
        seen.add(id)
        ordered.push(model)
      })
    }

    const appendDefault = () => {
      active.forEach(model => {
        if (model.defaultTier === tier) {
          appendByIds([model.id])
        }
      })
    }

    if (language) {
      // Pair-specific overrides first
      if (sourceLanguage) {
        let pairIds: string[] | undefined
        const targetKey = resolveLanguageKey(this.languagePairTiersCache, language)
        if (targetKey) {
          const sourceMap = this.languagePairTiersCache[targetKey]
          const sourceKey = resolveLanguageKey(sourceMap, sourceLanguage)
          if (sourceKey) {
            pairIds = sourceMap[sourceKey]?.[tier] || []
            appendByIds(pairIds)
          }
        }

        if (!pairIds || pairIds.length === 0) {
          active.forEach(model => {
            if (seen.has(model.id)) return
            const pairConfig = model.languagePairTiers
            if (!pairConfig) return
            const target = resolveLanguageKey(pairConfig, language)
            if (!target) return
            const sourceMap = pairConfig[target]
            const source = resolveLanguageKey(sourceMap, sourceLanguage)
            if (source && sourceMap?.[source] === tier) {
              appendByIds([model.id])
            }
          })
        }
      }

      // Destination-language overrides next
      let langOverrideIds: string[] | undefined
      const langKey = resolveLanguageKey(this.languageTiersCache, language)
      if (langKey) {
        langOverrideIds = this.languageTiersCache[langKey]?.[tier] || []
        appendByIds(langOverrideIds)
      }

      if (!langOverrideIds || langOverrideIds.length === 0) {
        active.forEach(model => {
          if (seen.has(model.id)) return
          const langMap = model.languageTiers
          if (!langMap) return
          const resolvedLang = resolveLanguageKey(langMap, language)
          if (resolvedLang && langMap[resolvedLang] === tier) {
            appendByIds([model.id])
          }
        })
      }

      if (ordered.length === 0) {
        appendDefault()
      }
    } else {
      appendDefault()
    }

    if (ordered.length === 0) {
      appendDefault()
    }

    return ordered
  }

  async getTierForModel(
    modelId: string,
    language?: string,
    sourceLanguage?: string,
    forceRefresh: boolean = false
  ): Promise<'Best' | 'Better' | 'Good' | 'None'> {
    await this.loadModels(forceRefresh)

    const model = await this.getModelById(modelId)
    if (!model) {
      return 'None'
    }

    if (language && sourceLanguage) {
      const targetKey = resolveLanguageKey(this.languagePairTiersCache, language)
      if (targetKey) {
        const sourceMap = this.languagePairTiersCache[targetKey]
        const sourceKey = resolveLanguageKey(sourceMap, sourceLanguage)
        if (sourceKey) {
          const tierMap = sourceMap[sourceKey] || {}
          const matchedTier = (['Best', 'Better', 'Good'] as const).find(
            currentTier => tierMap[currentTier]?.includes(modelId)
          )
          if (matchedTier) {
            return matchedTier
          }
        }
      }

      const pairConfig = model.languagePairTiers
      if (pairConfig) {
        const target = resolveLanguageKey(pairConfig, language)
        if (target) {
          const sourceMap = pairConfig[target]
          const source = resolveLanguageKey(sourceMap, sourceLanguage)
          const tier = source && sourceMap?.[source]
          if (tier && tier !== 'None') {
            return tier
          }
        }
      }
    }

    if (language) {
      const langKey = resolveLanguageKey(this.languageTiersCache, language)
      if (langKey) {
        const tierMap = this.languageTiersCache[langKey] || {}
        const matchedTier = (['Best', 'Better', 'Good'] as const).find(
          currentTier => tierMap[currentTier]?.includes(modelId)
        )
        if (matchedTier) {
          return matchedTier
        }
      }

      const langConfig = model.languageTiers
      if (langConfig) {
        const resolvedLang = resolveLanguageKey(langConfig, language)
        const tier = resolvedLang ? langConfig[resolvedLang] : undefined
        if (tier && tier !== 'None') {
          return tier
        }
      }

      if (isModelDisabledInLanguage(model, language)) {
        return 'None'
      }
    }

    return model.defaultTier || 'None'
  }

  clearCache(): void {
    this.modelsCache = []
    this.languageTiersCache = {}
    this.languagePairTiersCache = {}
    this.lastCacheUpdate = null
    this.loadingPromise = null
  }
}

export const aiModelService = new AIModelService()
